import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import logger from "../../utils/logger.js";
import {
  Wallet,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Gift,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import "./ProfileWalletTab.css";

export default function ProfileWalletTab({ isAr = false }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const t = useMemo(
    () => ({
      title: isAr ? "المحفظة" : "Wallet",
      subtitle: isAr ? "رصيدك وحركات النقاط" : "Your balance and coin activity",
      coins: isAr ? "النقاط" : "Coins",
      usd: isAr ? "القيمة بالدولار" : "USD Value",
      lifetimeEarned: isAr ? "إجمالي المكتسب" : "Lifetime Earned",
      lifetimeSpent: isAr ? "إجمالي المصروف" : "Lifetime Spent",
      dailyCheckin: isAr ? "تسجيل الحضور اليومي" : "Daily Check-in",
      dailyCheckinDesc: isAr
        ? "احصل على نقاط يومية عند تسجيل الحضور."
        : "Claim your daily reward coins.",
      checkinNow: isAr ? "سجّل الآن" : "Check in now",
      checkingIn: isAr ? "جارٍ التسجيل..." : "Checking in...",
      transactions: isAr ? "آخر الحركات" : "Recent transactions",
      noTransactions: isAr ? "لا توجد حركات بعد" : "No transactions yet",
      type: isAr ? "النوع" : "Type",
      amount: isAr ? "الكمية" : "Amount",
      note: isAr ? "ملاحظة" : "Note",
      date: isAr ? "التاريخ" : "Date",
      loading: isAr ? "جارٍ التحميل..." : "Loading...",
      loadError: isAr ? "فشل تحميل بيانات المحفظة" : "Failed to load wallet data",
      rulesTitle: isAr ? "كيف تكسب النقاط" : "How to earn coins",
      rules: [
        { label: isAr ? "تسجيل حساب" : "Signup bonus", value: "20" },
        { label: isAr ? "تسجيل يومي" : "Daily check-in", value: "10-70" },
        { label: isAr ? "كل 10$ شراء" : "Each $10 spent", value: "1" },
        { label: isAr ? "مراجعة منتج" : "Product review", value: "5" },
      ],
    }),
    [isAr]
  );

  async function loadWalletData() {
    try {
      setLoading(true);
      setError("");
      setMessage("");

      const [walletRes, txRes] = await Promise.all([
        api.apiGet("rewards/wallet"),
        api.apiGet("rewards/transactions"),
      ]);

      const walletData = walletRes?.data || walletRes?.wallet || walletRes || null;
      setWallet(walletData && typeof walletData === "object" ? walletData : null);

      const txList = txRes?.data || txRes?.transactions || txRes || [];
      setTransactions(Array.isArray(txList) ? txList : []);
    } catch (e) {
      logger.error("Wallet load error", e);
      setError(t.loadError);
      setWallet(null);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadWalletData();
  }, [isAr]);

  async function handleCheckin() {
    try {
      setCheckinLoading(true);
      setError("");
      setMessage("");

      const res = await api.apiPost("rewards/checkin", {});
      const payload = res?.data || {};

      if (res?.status === "success") {
        const coins = Number(payload?.coins || 0);
        const streak = Number(payload?.streak || 0);

        setMessage(
          isAr
            ? `تم تسجيل الحضور اليومي بنجاح. حصلت على ${coins} نقطة. السلسلة الحالية: ${streak}`
            : `Daily check-in successful. You earned ${coins} coins. Current streak: ${streak}`
        );

        await loadWalletData();
      } else {
        setError(
          res?.message ||
            (isAr
              ? "تعذر تسجيل الحضور اليومي."
              : "Unable to complete daily check-in.")
        );
      }
    } catch (e) {
      logger.error("Checkin error", e);
      setError(
        isAr ? "حدث خطأ أثناء تسجيل الحضور اليومي." : "Check-in failed."
      );
    } finally {
      setCheckinLoading(false);
    }
  }

  function formatTxType(type) {
    const map = {
      signup_bonus: isAr ? "مكافأة التسجيل" : "Signup bonus",
      daily_checkin: isAr ? "تسجيل يومي" : "Daily check-in",
      order_reward: isAr ? "مكافأة طلب" : "Order reward",
      redeem: isAr ? "استخدام نقاط" : "Redeem",
      review_reward: isAr ? "مكافأة مراجعة" : "Review reward",
      credit: isAr ? "إضافة" : "Credit",
      debit: isAr ? "خصم" : "Debit",
    };

    return map[type] || type || "-";
  }

  function formatDate(value) {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString(isAr ? "ar" : "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(value);
    }
  }

  function txAmountLabel(tx) {
    const amount = Number(tx?.amount || 0);
    const sign = amount > 0 ? "+" : "";
    return `${sign}${amount}`;
  }

  if (loading) {
    return (
      <div className="wallet-container" dir={isAr ? "rtl" : "ltr"}>
        <div className="wallet-loading">
          <div className="wallet-loading-spinner" />
          <p className="wallet-loading-text">{t.loading}</p>
        </div>
      </div>
    );
  }

  const coins = Number(wallet?.coins ?? wallet?.balance ?? 0);
  const usd = Number(wallet?.usd || 0);
  const lifetimeEarned = Number(wallet?.lifetime_earned || 0);
  const lifetimeSpent = Number(wallet?.lifetime_spent || 0);

  return (
    <div className="wallet-container" dir={isAr ? "rtl" : "ltr"}>
      <div className="wallet-header">
        <h2 className="wallet-header-title">{t.title}</h2>
        <p className="wallet-header-subtitle">{t.subtitle}</p>
      </div>

      {message && (
        <div className="wallet-message success">
          <CheckCircle size={18} style={{ display: "inline", marginInlineEnd: "8px", flexShrink: 0 }} />
          {message}
        </div>
      )}

      {error && (
        <div className="wallet-message error">
          <AlertCircle size={18} style={{ display: "inline", marginInlineEnd: "8px", flexShrink: 0 }} />
          {error}
        </div>
      )}

      <div className="wallet-stats-grid">
        <div className="wallet-stat-card">
          <div className="wallet-stat-icon">
            <Wallet size={28} strokeWidth={2} />
          </div>
          <div className="wallet-stat-label">{t.coins}</div>
          <div className="wallet-stat-value">{coins.toLocaleString()}</div>
        </div>

        <div className="wallet-stat-card">
          <div className="wallet-stat-icon">
            <DollarSign size={28} strokeWidth={2} />
          </div>
          <div className="wallet-stat-label">{t.usd}</div>
          <div className="wallet-stat-value">${usd.toFixed(2)}</div>
        </div>

        <div className="wallet-stat-card positive">
          <div className="wallet-stat-icon">
            <TrendingUp size={28} strokeWidth={2} />
          </div>
          <div className="wallet-stat-label">{t.lifetimeEarned}</div>
          <div className="wallet-stat-value">{lifetimeEarned.toLocaleString()}</div>
        </div>

        <div className="wallet-stat-card negative">
          <div className="wallet-stat-icon">
            <TrendingDown size={28} strokeWidth={2} />
          </div>
          <div className="wallet-stat-label">{t.lifetimeSpent}</div>
          <div className="wallet-stat-value">{lifetimeSpent.toLocaleString()}</div>
        </div>
      </div>

      <div className="wallet-section">
        <div className="wallet-section-header">
          <div className="wallet-section-icon">
            <Calendar size={20} strokeWidth={2} />
          </div>
          <h3 className="wallet-section-title">{t.dailyCheckin}</h3>
        </div>

        <div className="wallet-checkin-wrapper">
          <div className="wallet-checkin-text">
            <h4 className="wallet-checkin-title">{t.dailyCheckin}</h4>
            <p className="wallet-checkin-desc">{t.dailyCheckinDesc}</p>
          </div>

          <button
            type="button"
            className="wallet-checkin-button"
            onClick={handleCheckin}
            disabled={checkinLoading}
          >
            {checkinLoading ? t.checkingIn : t.checkinNow}
          </button>
        </div>
      </div>

      <div className="wallet-section">
        <div className="wallet-section-header">
          <div className="wallet-section-icon">
            <Gift size={20} strokeWidth={2} />
          </div>
          <h3 className="wallet-section-title">{t.rulesTitle}</h3>
        </div>

        <ul className="wallet-rules-list">
          {t.rules.map((rule, idx) => (
            <li key={idx} className="wallet-rules-item">
              <span className="wallet-rules-icon">{idx + 1}</span>
              <span>
                <strong>{rule.label}:</strong> {rule.value} coins
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="wallet-section">
        <div className="wallet-section-header">
          <div className="wallet-section-icon">
            <TrendingUp size={20} strokeWidth={2} />
          </div>
          <h3 className="wallet-section-title">{t.transactions}</h3>
        </div>

        {transactions.length === 0 ? (
          <div className="wallet-empty">
            <div className="wallet-empty-icon">
              <Wallet size={28} strokeWidth={1.5} />
            </div>
            <p className="wallet-empty-text">{t.noTransactions}</p>
          </div>
        ) : (
          <div className="wallet-transactions-wrapper">
            <table className="wallet-transactions-table">
              <thead>
                <tr>
                  <th>{t.type}</th>
                  <th>{t.amount}</th>
                  <th>{t.note}</th>
                  <th>{t.date}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const amount = Number(tx?.amount || 0);
                  const isCredit = amount >= 0;
                  return (
                    <tr key={tx.id}>
                      <td>
                        <span className="wallet-transactions-type">
                          {formatTxType(tx.type || tx.source || tx.tx_type)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`wallet-transactions-amount ${
                            isCredit ? "credit" : "debit"
                          }`}
                        >
                          {txAmountLabel(tx)}
                        </span>
                      </td>
                      <td>{tx.note || "-"}</td>
                      <td>{formatDate(tx.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}